import { GetServerSidePropsContext } from 'next';
import React from 'react';
import ShopSearchHead from 'components/ShopSearchHead/ShopSearchHead';
import { DefaultPage } from 'styles/Page.styles';
import BasicLayout from 'layouts/BasicLayout';
import {
  CategoryPageInputProps,
  CategoryPageProps
} from 'models/CategoryPageProps';
import { GetInitialCategoryPageProps } from 'models/GetInitialCategoryPageProps';
import { FilterableQueryParams } from 'models/FilterableQueryParams';
import { ShopSearchRawQueryParams } from 'models/ShopSearchRawQueryParams';
import { normalizeShopSearchRawQuery } from 'helpers/normalizeShopSearchRawQuery';
import { getModals } from 'helpers/getModals';
import { getResultPageComponents } from 'helpers/getResultPageComponents';
import { getSearchResults } from 'helpers/getSearchResults';
import {
  determineEnvironmentFromHost,
  determineHost,
  determineTargetMarketFromHost
} from 'helpers/url-mapper';
import { useIntl } from 'react-intl';
import { TranslateFunction } from 'models/TranslateFunction';
import { getPageLinks } from 'helpers/getPageLinks';
import { STATUS_CODES } from 'helpers/statusCodes';
import { getKeywordsOfCategory } from 'helpers/getKeywordsOfCategory';
import { getActiveKeyword } from 'helpers/getActiveKeyword';
import { processKeywords } from 'helpers/processKeywords';
import { emptyPage } from 'helpers/emptyPage';
import { isEmbedActive } from 'helpers/isEmbedActive';

const getInitialProps: GetInitialCategoryPageProps = async (
  context: GetServerSidePropsContext
) => {
  const host = determineHost(context);
  const environment = determineEnvironmentFromHost(host);
  const targetMarket = determineTargetMarketFromHost(host);

  const [pageLinks, searchResults, keywords] = await Promise.all([
    getPageLinks(context, environment).catch((errorStatusCode) => {
      context.res.statusCode = errorStatusCode;
      return [];
    }),
    getSearchResults(context, environment, targetMarket).catch(
      (errorStatusCode) => {
        /* istanbul ignore next */
        if (context && context.res) {
          context.res.statusCode = errorStatusCode;
        }
        return emptyPage;
      }
    ) as Promise<CategoryPageProps>,
    getKeywordsOfCategory(context, environment, targetMarket).catch(
      (errorStatusCode) => {
        context.res.statusCode = errorStatusCode;
        return [];
      }
    )
  ]);

  if (
    (!context.res || context.res.statusCode < STATUS_CODES.BAD_REQUEST) &&
    !searchResults.category
  ) {
    context.res.statusCode = STATUS_CODES.GONE;
  }

  searchResults.keyword = getActiveKeyword(
    normalizeShopSearchRawQuery(context.query as ShopSearchRawQueryParams),
    keywords
  );

  /* istanbul ignore else */
  if (environment !== 'vrt') {
    processKeywords(keywords);
  }

  return {
    ...searchResults,
    account: undefined,
    host,
    pageLinks,
    keywords
  };
};

CategoryPage.getInitialProps = getInitialProps;

export function CategoryPage({
  shops,
  category,
  keyword,
  keywords,
  searchTerm,
  activePage,
  hasValidCertificate,
  sort,
  metadata,
  queryParams,
  categoryList,
  pageLinks,
  setModalVisible,
  modalVisible,
  mobileMenuOpen,
  setMobileMenuOpen,
  onParameterChange,
  host
}: CategoryPageInputProps) {
  const { formatMessage } = useIntl();
  const translate: TranslateFunction = (id: string) => formatMessage({ id });

  const activeFilters: FilterableQueryParams = {
    q: searchTerm,
    page: activePage || undefined,
    sort: sort,
    hasValidCertificate,
    category: category?.urlPath,
    keyword: keyword?.urlPath,
    ...(isEmbedActive(queryParams.embed) ? { embed: true } : {})
  };

  return (
    <BasicLayout noScrolling={!!modalVisible || mobileMenuOpen}>
      <DefaultPage $hasAside={!!categoryList && categoryList.length > 0}>
        <ShopSearchHead
          {...{
            category,
            keyword,
            totalShopCount: metadata.totalShopCount,
            queryParams,
            pageLinks,
            host
          }}
        />
        {getResultPageComponents({
          activeFilters,
          onParameterChange,
          category,
          keyword: keyword?.name,
          keywords,
          setMobileMenuOpen,
          mobileMenuOpen,
          metadata,
          setModalVisible,
          categoryList,
          shops
        })}
      </DefaultPage>
      {getModals({
        modalVisible,
        setModalVisible,
        categoryList,
        activeFilters,
        onParameterChange,
        translate
      })}
    </BasicLayout>
  );
}

export default CategoryPage;
